/**
 * 福島県の料理12品。レベルが上がるほど大きく、器つきの汁物・鍋物になる。
 *
 * 選定のときに迷った点を残しておく。
 * - 「はっとう」はそば粉を使うが麺ではなく、3〜5ミリに伸ばしてひし形に切った生地である。
 *   そばが他県に多いので迷ったが、形がまったく別なので入れた。
 * - 「どんこ汁」は岩手にも同名の料理があるが、岩手側は別の汁物に差し替えて福島に置いた。
 * - 「こづゆ」と「のっぺ」（新潟）は公的な出典を読み比べた。こづゆは貝柱の出汁で具を7種か9種の
 *   奇数にし、会津塗の赤い手塩皿に盛る。器そのものが違うので両方入れている。
 * - 「にしんの山椒漬け」と石川の「大根ずし」はどちらも保存のための魚の漬け物だが、
 *   福島は身欠きにしんと山椒の葉を交互に重ねて酢と醤油で漬ける。別物として扱った。
 * - 「あんぽ柿のなます」は生の柿ではなく干し柿を千切りにして酢で和えた惣菜なので、
 *   岐阜の富有柿とは重ならない。
 * - 喜多方ラーメンと円盤餃子は、麺と餃子が他県に多いので入れていない。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：じゅうねんぼたもち・あんぽ柿のなます・はっとう・つゆ煮しめ・どんこ汁（店で食べられる品に差し替え）
 * - 福島円盤餃子：文化庁の資料には始めた人の名と店名が書かれているが、特定の店の宣伝にならないよう書かない
 * - 福島円盤餃子：加盟店の数（資料では14店、会のサイトでは11店）は変わる情報なので書かない
 * - 福島円盤餃子：「すき間なく並べ」は資料の「円盤状に餃子を焼き上げている」「フライパン1枚で再現」からの言い換え。資料には店によってストレートや円盤型など並べ方がいろいろあるとも書かれている
 * - なみえ焼そば：考案された時期は、文化庁の一覧では「約65年前」、浪江町商工会のページでは「約50年前」と食い違うので書かない
 * - なみえ焼そば：震災後に町外の避難先で提供が続いた話は、開いた公式資料に書かれていないので書かない
 * - なみえ焼そば：B-1グランプリの受賞歴は料理の説明ではないので書かない
 * - 喜多方ラーメン：「日本三大ラーメン」は開いた公式資料に書かれていないので書かない
 * - 喜多方ラーメン：朝からラーメンを食べる習慣（朝ラー）は、開いた公式資料で確かめていないので書かない
 * - 喜多方ラーメン：チャーシューが丼を覆う盛り付けは、公式資料に書かれていないので書かない
 * - 会津ソースカツ丼：起源の3つの説（大正のカフェのまかない、戦前に東京から洋食のコックを招いた、戦後に東京で出会った味をアレンジ）は会自身も「諸説」としているので、中身は書かず「諸説あり解明されていない」とだけ書く
 * - 会津ソースカツ丼：煮込みソースカツ丼など店ごとの派生は、店の宣伝になるので書かない
 * - 会津の馬刺し：生で食べる習慣とからし味噌が「人気プロレスラー」をきっかけに広まったという話は、農水省のページにあるが、人物の特定にかかわる伝聞なので書かない
 * - 会津の馬刺し：農水省のページには「基本的には家庭内で食べるもので、外食で楽しむケースは少ない」ともある。ゲームで店を案内することと食い違って見えるので、説明文には入れない
 * - 会津の馬刺し：段の並びは「上の段ほど、旅先で特別な日に食べるごちそうのイメージ」で組んでいる（料理の格付けではない。図鑑にもそう書いている）。
 *   馬刺しは会津でふだんの食卓にも上る料理なので、12段目は特別な日のごちそうのイメージが強い品（福島牛の案）にし、馬刺しは下の段へ移す予定。
 *   今回のリリースは Lv12 のまま出し、次回直す（2026-10-02 相島さん決定）。
 *   ※値段の高い安いで段を決めたと読める書き方はしない（画面の「格付けではありません」と食い違うため）
  * - 2026-10-12 地元民レビューで「しんごろう」を外し「白河ラーメン」を入れた（地元の人がまず挙げる品のため。旧IDは idMigrations の retiredItems）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { fukushimaSceneryTour } from './fukushimaScenes';
import { FUKUSHIMA_FOOD_HULLS } from './fukushimaFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'miso-kanpura',
    level: 1,
    name: 'みそかんぷら',
    shortName: 'みそかんぷら',
    image: '/assets/foods/fukushima/01_miso_kanpura.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '出荷できないほど小さなじゃがいもを、皮のまま油で炒めて水で柔らかく煮て、砂糖とみりんと味噌をからめる。「かんぷら」は、じゃがいものオランダ語がなまったものと考えられている。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/30_9_fukushima.html',
    icon: { label: 'みそかんぷら', shape: 'berry', baseColor: '#8a5a2a', accentColor: '#d9b268' },
    reward: {
      unlockScore: 200,
      area: '古殿町',
      headline: '小さすぎる芋の使いみち',
      tip: '7月下旬の収穫期から食べられ、冬のあいだの保存芋でも作られていた。農家が家で食べるために考えた料理。',
      links: searchLinks('古殿町', 'みそかんぷら'),
    },
  },
  {
    id: 'ika-ninjin',
    level: 2,
    name: 'いかにんじん',
    shortName: 'いかにんじん',
    image: '/assets/foods/fukushima/02_ika_ninjin.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: 'するめいかと人参を同じくらいの長さの細切りにし、砂糖と酒と醤油を煮立てて冷ましたたれに漬ける。半日ほどで食べられ、数日漬けるとさらに味がしみる。もとは雪の多い冬の保存食だった。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/30_1_fukushima.html',
    icon: { label: 'いかにんじん', shape: 'roe', baseColor: '#d9702a', accentColor: '#e8d9b8' },
    reward: {
      unlockScore: 500,
      area: '福島市',
      headline: '細切りが絡み合う',
      tip: '100年以上前から食べられていて、家庭ごとに味つけが違う。北海道の松前漬との関わりが指摘されるが、松前漬には昆布が入る点が異なる。',
      links: searchLinks('福島市', 'いかにんじん'),
    },
  },
  {
    id: 'nishin-sansho',
    level: 3,
    name: 'にしんの山椒漬け',
    shortName: 'にしんの山椒漬け',
    image: '/assets/foods/fukushima/03_nishin_sansho.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '洗って頭を取った身欠きにしんを、山椒の葉を敷いた鉢にぎっしり並べ、山椒の葉、にしんの順に重ねる。上から葉をかぶせ、酢と酒と醤油を入れて重石をし、数日から一週間置く。山椒が採れる春から初夏に食べる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/30_3_fukushima.html',
    icon: { label: 'にしんの山椒漬け', shape: 'fish', baseColor: '#6b5a3a', accentColor: '#4c7a2c' },
    reward: {
      unlockScore: 1000,
      area: '会津若松市ほか会津地方',
      headline: 'にしんと山椒の葉を交互に',
      tip: '江戸時代、北海道産のにしんを乾かした「みがきにしん」が会津に流通し、保存がきいてたんぱく源になることから重宝された。硬い「本乾」と少し柔らかい「半乾」があり、昔は本乾を使うことが多かった。',
      links: searchLinks('会津若松市', 'にしんの山椒漬け'),
    },
  },
  {
    id: 'shirakawa-ramen',
    level: 4,
    name: '白河ラーメン',
    shortName: '白河ラーメン',
    image: '/assets/foods/fukushima/04_shirakawa_ramen.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '豚骨・鶏がらベースの、濃厚でありながらさっぱりとした醤油スープに、手打ちの太い縮れ麺を合わせる。具には肉厚のチャーシューが欠かせない。',
    source:
      'https://www.pref.fukushima.lg.jp/sec/01230a/taberu.html',
    icon: { label: '白河', shape: 'ramen', baseColor: '#c88a4a', accentColor: '#e8c070' },
    reward: {
      unlockScore: 1800,
      area: '白河市',
      headline: '手打ちの縮れ麺と醤油スープ',
      tip: '白河市内には約100の店があり、伝統を守りながら、それぞれの店が技と味を競っている。',
      links: searchLinks('白河市', '白河ラーメン'),
      official: [{ label: '白河観光物産協会「白河ラーメン」', url: 'https://shirakawa315.com/ramen/' }],
    },
  },
  {
    id: 'fukushima-enban-gyoza',
    level: 5,
    name: '福島円盤餃子',
    shortName: '円盤餃子',
    image: '/assets/foods/fukushima/05_fukushima_enban_gyoza.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: 'フライパン1枚に餃子をすき間なく並べ、円盤の形に焼き上げる。戦後の昭和28年、満州から引き揚げた人が生計を立てるために始めた屋台で、満州で学んだ水餃子をフライパン1枚で再現したのが始まりとされる。',
    source:
      'https://www.bunka.go.jp/seisaku/shokubunka/foodculture/assets/pdf/hyakunenfood/jirei/pdf_492.pdf',
    icon: { label: '円盤餃子', shape: 'dumpling', baseColor: '#d9a35a', accentColor: '#8a5a2b' },
    reward: {
      unlockScore: 3000,
      area: '福島市',
      headline: 'フライパンの形のまま焼く餃子',
      tip: '一皿20〜30個と数が多いので、たくさん食べられるよう野菜をたっぷり使い、軽い食感に仕上げる。昔から仕事帰りの客が多く、夕方から開ける店が多い。',
      links: searchLinks('福島市', '円盤餃子'),
      official: [{ label: 'ふくしま餃子の会', url: 'https://fukushima-gyoza.com/' }],
    },
  },
  {
    id: 'namie-yakisoba',
    level: 6,
    name: 'なみえ焼そば',
    shortName: 'なみえ焼そば',
    image: '/assets/foods/fukushima/06_namie_yakisoba.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: 'ふつうの約3倍もある極太の麺に、うまみたっぷりの濃厚なソース。具は豚肉ともやしだけというシンプルな焼そば。労働者のために、食べごたえと腹持ちをよくしようと考えられたとされる、浪江町で昔から親しまれてきた味。',
    source:
      'https://www.bunka.go.jp/seisaku/shokubunka/foodculture/hyakunenfood/jirei/list_fukushima.html',
    icon: { label: '焼そば', shape: 'noodle-bowl', baseColor: '#6b3f1f', accentColor: '#e0442b' },
    reward: {
      unlockScore: 4500,
      area: '浪江町',
      headline: 'ふつうの3倍の太さの麺',
      tip: '地元の商工会は「中華麺の太麺を使う」「具はもやしと豚肉が基本」「ソース味」を定義としている。一味唐辛子を振りかけて食べるのが通な食べ方とされる。',
      links: searchLinks('浪江町', 'なみえ焼そば'),
      official: [{ label: '浪江町商工会「なみえ焼そば」', url: 'https://r.goope.jp/nsci/free/namieyakisoba' }],
    },
  },
  {
    id: 'popo-yaki',
    level: 7,
    name: 'サンマのポーポー焼き',
    shortName: 'サンマのポーポー焼き',
    image: '/assets/foods/fukushima/07_popo_yaki.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: 'サンマの頭と内臓、皮と骨を取ってまな板の上で包丁でたたき、ねぎと生姜と調味料を混ぜてハンバーグのように丸めて焼く。もとは漁師が船の上で食べる料理だった。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/30_4_fukushima.html',
    icon: { label: 'サンマのポーポー焼き', shape: 'dumpling', baseColor: '#8a5730', accentColor: '#c9a45e' },
    reward: {
      unlockScore: 6500,
      area: 'いわき市',
      headline: '油でポーポー燃えるから',
      tip: 'サンマを炭で焼くときに、サンマの油で火がポーポーと燃えあがることからこの名がついたと言われる。秋の料理。',
      links: searchLinks('いわき市', 'サンマのポーポー焼き'),
    },
  },
  {
    id: 'kozuyu',
    level: 8,
    name: 'こづゆ',
    shortName: 'こづゆ',
    image: '/assets/foods/fukushima/08_kozuyu.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '干ししいたけと貝柱の出汁で、きくらげ、里芋、糸こんにゃく、白玉麩、人参、わらびなどを煮て、醤油と塩で調える。具は7種か9種の奇数にするのが縁起がよいとされる。会津塗の赤い漆の手塩皿に盛る。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/30_2_fukushima.html',
    icon: { label: 'こづゆ', shape: 'rice-bowl', baseColor: '#b02a20', accentColor: '#e8dcc0' },
    reward: {
      unlockScore: 9000,
      area: '会津若松市ほか会津地方',
      headline: '器まで決まっている',
      tip: '「こじゅうのつゆ」がなまった名とされる。100年以上前から食べられ、昔は二つの椀に分けて出していたが、昭和60年代ごろからひとつの椀で出すようになった。婚礼には欠かせない。',
      links: searchLinks('会津若松市', 'こづゆ'),
    },
  },
  {
    id: 'kitakata-ramen',
    level: 9,
    name: '喜多方ラーメン',
    shortName: '喜多方ラーメン',
    image: '/assets/foods/fukushima/09_kitakata_ramen.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '麺は幅約4mmの太麺で、水分を多く含ませてじっくり寝かせる「平打ち熟成多加水麺」。コシと独特の縮れがある。大正末期、中国から渡ってきた青年が屋台を引いて支那そばを売り歩いたのが始まりと言われる。',
    source:
      'https://www.bunka.go.jp/seisaku/shokubunka/foodculture/hyakunenfood/jirei/list_fukushima.html',
    icon: { label: 'ラーメン', shape: 'ramen', baseColor: '#c9934a', accentColor: '#f1e2c4' },
    reward: {
      unlockScore: 12000,
      area: '喜多方市',
      headline: '平たく縮れた太い多加水麺',
      tip: 'スープは醤油味が基本だが、店によって塩や味噌もある。麺とスープには飯豊連峰からの伏流水や、平成の名水百選に選ばれた「栂峰渓流水」が多く使われている。',
      links: searchLinks('喜多方市', '喜多方ラーメン'),
    },
  },
  {
    id: 'koi-kanroni',
    level: 10,
    name: '鯉の甘露煮',
    shortName: '鯉の甘露煮',
    image: '/assets/foods/fukushima/10_koi_kanroni.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '下処理してぶつ切りにした鯉を、たっぷりの砂糖と水あめ、醤油と酒で数時間アクを取りながら煮込む。火を止めて冷ましながら煮汁をかけて含ませ、一晩おいて翌日温め直す。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/30_6_fukushima.html',
    icon: { label: '鯉の甘露煮', shape: 'curry-plate', baseColor: '#4a2415', accentColor: '#b07a30' },
    reward: {
      unlockScore: 16000,
      area: '会津地方',
      headline: '猪苗代湖から引いた水で育つ',
      tip: '明治になると猪苗代湖から水を引いて溜池をつくり、各地の士族が鯉の養殖を始めた。養蚕がさかんで、鯉のえさになる蚕のさなぎが手に入りやすかったことも生産が増えた理由のひとつ。',
      links: searchLinks('会津若松市', '鯉の甘露煮'),
    },
  },
  {
    id: 'aizu-sauce-katsudon',
    level: 11,
    name: '会津ソースカツ丼',
    shortName: 'ソースカツ丼',
    image: '/assets/foods/fukushima/11_aizu_sauce_katsudon.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: 'ご飯の上に千切りキャベツを敷き、その上にソースにひたしたとんかつをのせる。大正時代から親しまれてきた会津の庶民の味とされ、会津でカツ丼といえばソース味が当たり前だという。',
    source:
      'https://aizu-katudon.com/',
    icon: { label: 'カツ丼', shape: 'rice-bowl', baseColor: '#7a4a26', accentColor: '#6fae3f' },
    reward: {
      unlockScore: 22000,
      area: '会津若松市ほか会津地方',
      headline: '会津のカツ丼はソース味',
      tip: 'なぜソース味なのかは、大正起源説、昭和の戦前説、戦後説などがあり、由来はまだ解明されていない。2004年に町の食堂が集まり「伝統会津ソースカツ丼の会」ができた。',
      links: searchLinks('会津若松市', 'ソースカツ丼'),
      official: [{ label: '伝統会津ソースカツ丼の会「会津のソースカツ丼」', url: 'https://aizu-katudon.com/source_katsu/' }],
    },
  },
  {
    id: 'aizu-basashi',
    level: 12,
    name: '会津の馬刺し',
    shortName: '会津の馬刺し',
    image: '/assets/foods/fukushima/12_aizu_basashi.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '会津若松の馬肉は赤身で、脂肪の「さし」が入っていないのが特徴。しょうゆと、からし味噌をつけて食べる。福島に馬肉食が広まったのは戊辰戦争の頃で、運ばれてきた負傷者に馬肉を食べさせたのが始まりとされる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/30_23_fukushima.html',
    icon: { label: '馬刺し', shape: 'sashimi-platter', baseColor: '#a3242f', accentColor: '#e0662b' },
    reward: {
      unlockScore: 30000,
      area: '会津若松市／会津坂下町',
      headline: 'さしの入らない赤身をからし味噌で',
      tip: '内陸で新鮮な生魚が入りにくかった会津若松では、「刺身」といえば魚ではなく馬刺しを指すと言われるほど。8月29日の「馬肉の日」には市内の飲食店で馬肉の定食が出る。',
      links: searchLinks('会津若松市', '馬刺し'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const fukushimaFoodStage: StageDefinition = {
  regionId: 'fukushima',
  regionName: '福島県',
  mode: 'food',
  title: '福島めしマージ',
  theme: {
    primaryColor: '#b03a2e',
    secondaryColor: '#f1e7d8',
    accentColor: '#c8973c',
    backgroundColor: '#fcf6ec',
  },
  /** 背景ツアー「福島めぐり」。軸は季節。4月の三春滝桜から始めて、2月の大内宿で終わる。会津・中通り・浜通りをひととおり通る。 */
  scenery: {
    tour: fukushimaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, FUKUSHIMA_FOOD_HULLS),
};
